import type {
  RunActivity,
  RunActivityKey,
  RunAllowedAction,
  RunControlRequest,
  RunExecutionHealth,
  RunFailure,
  RunSandboxLifecycle,
  RunSuspension,
  RunUserHold,
} from '@trevize/db'

export interface ImplementationActivityInput {
  workflowVersion: number | null
  runStatus: string
  runError: string | null
  enginePhase: string | null
  currentStepName: string | null
  currentStepStatus: string | null
  runExecutionStatus: string | null
  suspension: RunSuspension | null
  userHold: RunUserHold | null
  controlRequest: RunControlRequest | null
  sandboxLifecycle: RunSandboxLifecycle | null
  executionHealth: RunExecutionHealth | null
  failure: RunFailure | null
  branchPushed: boolean
  prStatus: string | null
  startedAt: Date | string | null
  updatedAt: Date | string | null
}

export interface DerivedImplementationActivity {
  activity: RunActivity
  availableActions: RunAllowedAction[]
}

interface ActivityMatch {
  key: RunActivityKey
  matches: (input: ImplementationActivityInput) => boolean
}

/**
 * Highest-priority fact wins. Terminal lifecycle and cooperative control
 * acknowledgements intentionally outrank resource, health, and wait facts.
 */
export const blueprintWorkflowActivityPriority: readonly ActivityMatch[] = [
  {
    key: 'merged',
    matches: (input) =>
      input.runStatus === 'completed' && input.prStatus === 'merged',
  },
  {
    key: 'cancelled',
    matches: (input) => input.runStatus === 'cancelled',
  },
  {
    key: 'failed',
    matches: (input) => input.runStatus === 'failed',
  },
  {
    key: 'completed',
    matches: (input) => input.runStatus === 'completed',
  },
  {
    key: 'cancelling',
    matches: (input) => input.controlRequest?.type === 'cancel_requested',
  },
  {
    key: 'finalizing',
    matches: (input) => input.enginePhase === 'finalizing',
  },
  {
    /**
     * Covers the full pause window: the acknowledged-but-not-yet-held gap must
     * not fall back to `working`, or a second pause request could overwrite the
     * acknowledged one and never be acknowledged itself.
     */
    key: 'pausing',
    matches: (input) =>
      input.controlRequest?.type === 'pause_requested' &&
      input.userHold === null,
  },
  {
    key: 'resuming',
    matches: (input) => input.userHold?.resumeRequest !== undefined,
  },
  {
    key: 'paused',
    matches: (input) => input.userHold !== null,
  },
  {
    key: 'needs_attention',
    matches: (input) =>
      input.executionHealth === 'stalled' ||
      input.sandboxLifecycle === 'failed' ||
      input.suspension?.cause === 'action_required' ||
      (input.suspension?.cause === 'input' &&
        (input.currentStepName === 'verify' ||
          input.suspension.details.attentionReason !== undefined)),
  },
  {
    key: 'waking_workspace',
    matches: (input) =>
      input.sandboxLifecycle === 'restoring' ||
      input.executionHealth === 'recovering',
  },
  {
    key: 'needs_input',
    matches: (input) => input.suspension?.cause === 'input',
  },
  {
    key: 'awaiting_approval',
    matches: (input) => input.suspension?.cause === 'approval',
  },
  {
    key: 'rate_limited',
    matches: (input) => input.suspension?.cause === 'usage_limit',
  },
  {
    key: 'waiting_for_provider',
    matches: (input) => input.suspension?.cause === 'provider_unavailable',
  },
  {
    key: 'ready_for_review',
    matches: (input) =>
      input.currentStepName === 'ready_for_review' ||
      input.runExecutionStatus === 'ready' ||
      (input.suspension?.cause === 'external_event' &&
        input.suspension.details.kind === 'user_feedback'),
  },
  {
    key: 'waiting_for_ci',
    matches: (input) =>
      input.suspension?.cause === 'external_event' &&
      input.suspension.details.kind === 'ci',
  },
  {
    key: 'waiting_for_external_event',
    matches: (input) => input.suspension?.cause === 'external_event',
  },
  {
    key: 'preparing_workspace',
    matches: (input) =>
      input.enginePhase === 'initializing' ||
      input.sandboxLifecycle === 'provisioning',
  },
  {
    key: 'queued',
    matches: (input) =>
      input.enginePhase === 'queued' ||
      input.currentStepStatus === 'queued' ||
      input.runStatus === 'queued' ||
      input.runStatus === 'pending',
  },
  {
    key: 'working',
    matches: () => true,
  },
]

const activityLabels: Readonly<Record<RunActivityKey, string>> = {
  queued: 'Queued',
  preparing_workspace: 'Preparing workspace',
  working: 'Working',
  pausing: 'Pausing',
  paused: 'Paused',
  resuming: 'Resuming',
  needs_input: 'Needs input',
  awaiting_approval: 'Awaiting approval',
  rate_limited: 'Rate limited',
  waiting_for_provider: 'Provider unavailable',
  waiting_for_ci: 'Waiting for CI',
  waiting_for_external_event: 'Waiting for external event',
  ready_for_review: 'Ready for review',
  needs_attention: 'Needs attention',
  waking_workspace: 'Waking workspace',
  finalizing: 'Finalizing',
  cancelling: 'Cancelling',
  completed: 'Completed',
  failed: 'Failed',
  stopped: 'Stopped',
  cancelled: 'Cancelled',
  merged: 'Merged',
}

function timestamp(value: Date | string | null): string | null {
  if (!value) {
    return null
  }
  return value instanceof Date ? value.toISOString() : value
}

function activitySince(
  key: RunActivityKey,
  input: ImplementationActivityInput,
): string | null {
  if (key === 'pausing' || key === 'cancelling') {
    return input.controlRequest?.requestedAt ?? null
  }
  if (key === 'paused') {
    return input.userHold?.startedAt ?? null
  }
  if (key === 'resuming') {
    return input.userHold?.resumeRequest?.requestedAt ?? null
  }
  if (
    key === 'needs_input' ||
    key === 'awaiting_approval' ||
    key === 'rate_limited' ||
    key === 'waiting_for_provider' ||
    key === 'waiting_for_ci' ||
    key === 'waiting_for_external_event' ||
    key === 'ready_for_review' ||
    key === 'needs_attention'
  ) {
    return input.suspension?.startedAt ?? timestamp(input.updatedAt)
  }
  return timestamp(input.updatedAt) ?? timestamp(input.startedAt)
}

function activityResumeAt(input: ImplementationActivityInput): string | null {
  const suspension = input.suspension
  if (!suspension) {
    return null
  }
  if (suspension.cause === 'usage_limit') {
    return suspension.details.resetAt
  }
  if (suspension.cause === 'provider_unavailable') {
    return suspension.details.retryAt
  }
  return suspension.wakeAfter ?? null
}

function activityDetail(
  key: RunActivityKey,
  input: ImplementationActivityInput,
): string | null {
  if (key === 'working' && input.currentStepName) {
    return input.currentStepName.replaceAll('_', ' ')
  }
  if (key === 'needs_attention') {
    if (
      input.suspension?.cause === 'input' &&
      (input.currentStepName === 'verify' ||
        input.suspension.details.attentionReason !== undefined)
    ) {
      return input.suspension.details.prompt ?? null
    }
    if (input.suspension?.cause === 'action_required') {
      return input.suspension.details.reason
    }
    return input.failure?.message ?? input.executionHealth ?? null
  }
  if (key === 'needs_input' && input.suspension?.cause === 'input') {
    return input.suspension.details.prompt ?? null
  }
  if (key === 'awaiting_approval' && input.suspension?.cause === 'approval') {
    return input.suspension.details.summary
  }
  if (
    key === 'waiting_for_provider' &&
    input.suspension?.cause === 'provider_unavailable'
  ) {
    return input.suspension.details.message
  }
  return null
}

function appendUnique(
  actions: RunAllowedAction[],
  action: RunAllowedAction,
): void {
  if (!actions.includes(action)) {
    actions.push(action)
  }
}

function actionRequiredActions(
  input: ImplementationActivityInput,
): RunAllowedAction[] {
  if (input.suspension?.cause !== 'action_required') {
    return []
  }
  return [...input.suspension.details.allowedActions]
}

export type TerminalSandboxFailureInput = Pick<
  ImplementationActivityInput,
  'failure' | 'runError' | 'sandboxLifecycle'
>

export function isTerminalSandboxFailure(
  input: TerminalSandboxFailureInput,
): boolean {
  const failure = input.failure
  const runError = input.runError?.toLowerCase()
  const terminalSandboxError =
    runError?.includes('sandbox') &&
    (runError.includes('non-recoverable') ||
      runError.includes('unrecoverable') ||
      runError.includes('cannot be restarted') ||
      runError.includes('session not found'))
  return (
    input.sandboxLifecycle === 'failed' ||
    terminalSandboxError === true ||
    failure?.failureKind === 'resource' ||
    (failure?.failureKind === 'initialization' &&
      failure.terminalReason === 'initialization_setup_sandbox')
  )
}

function terminalActions(
  key: RunActivityKey,
  input: ImplementationActivityInput,
): RunAllowedAction[] | null {
  if (key === 'failed') {
    if (isTerminalSandboxFailure(input)) {
      return input.branchPushed
        ? ['recover', 'archive']
        : ['restart', 'archive']
    }
    return ['recover', 'restart', 'archive']
  }
  if (key === 'cancelled') {
    return ['restart', 'archive']
  }
  if (key === 'completed' || key === 'merged') {
    return ['send_feedback', 'archive']
  }
  return null
}

function blueprintWorkflowActions(
  key: RunActivityKey,
  input: ImplementationActivityInput,
): RunAllowedAction[] {
  const terminal = terminalActions(key, input)
  if (terminal) {
    return terminal
  }
  if (key === 'cancelling' || key === 'finalizing') {
    return []
  }
  if (key === 'pausing' || key === 'resuming') {
    return ['cancel']
  }
  if (key === 'paused') {
    return ['resume', 'send_feedback', 'cancel']
  }
  if (
    input.enginePhase === 'initializing' ||
    input.sandboxLifecycle === 'provisioning'
  ) {
    return ['cancel']
  }
  if (key === 'ready_for_review') {
    return ['send_feedback', 'finish', 'archive']
  }

  const actions = actionRequiredActions(input)
  if (key === 'needs_input') {
    appendUnique(actions, 'provide_input')
  }
  if (
    key === 'needs_attention' &&
    input.suspension?.cause === 'input' &&
    (input.currentStepName === 'verify' ||
      input.suspension.details.attentionReason !== undefined)
  ) {
    appendUnique(actions, 'provide_input')
  }
  if (key === 'awaiting_approval') {
    appendUnique(actions, 'approve')
    appendUnique(actions, 'provide_input')
  }
  const isProviderWait =
    key === 'rate_limited' || key === 'waiting_for_provider'
  if (isProviderWait) {
    appendUnique(actions, 'retry')
  }
  if (
    isProviderWait ||
    key === 'working' ||
    key === 'needs_input' ||
    key === 'awaiting_approval' ||
    key === 'waiting_for_ci' ||
    (key === 'needs_attention' &&
      input.suspension?.cause === 'input' &&
      (input.currentStepName === 'verify' ||
        input.suspension.details.attentionReason !== undefined)) ||
    (key === 'needs_attention' &&
      input.suspension?.cause === 'action_required' &&
      input.suspension.correlationId?.endsWith(':ci') === true)
  ) {
    appendUnique(actions, 'send_feedback')
  }
  if (
    !isProviderWait &&
    key !== 'needs_attention' &&
    key !== 'waiting_for_ci'
  ) {
    appendUnique(actions, 'pause')
  }
  appendUnique(actions, 'cancel')
  return actions
}

function legacyKey(input: ImplementationActivityInput): RunActivityKey {
  if (input.runStatus === 'merged' || input.prStatus === 'merged') {
    return 'merged'
  }
  if (input.runStatus === 'cancelled') {
    return 'cancelled'
  }
  if (input.runStatus === 'failed') {
    return 'failed'
  }
  if (input.runStatus === 'stopped') {
    return 'stopped'
  }
  if (input.runStatus === 'completed') {
    return 'completed'
  }
  if (input.currentStepStatus === 'waiting_for_input') {
    return 'needs_input'
  }
  if (input.currentStepStatus === 'awaiting_approval') {
    return 'awaiting_approval'
  }
  if (input.runStatus === 'pending' || input.runStatus === 'queued') {
    return 'queued'
  }
  return 'working'
}

const legacyStepActivityLabels: Readonly<Record<string, string>> = {
  setup_repo: 'Setting up sandbox',
  plan: 'Planning',
  analyze: 'Analyzing',
  implement: 'Implementing',
  implement_fix: 'Fixing',
  test: 'Testing',
  verify: 'Verifying',
  open_pr: 'Opening PR',
  await_ci: 'Waiting for CI',
  ready_for_review: 'Ready for review',
  process_feedback: 'Processing feedback',
}

function activityLabel(
  key: RunActivityKey,
  input: ImplementationActivityInput,
  isBlueprintWorkflow: boolean,
): string {
  if (!isBlueprintWorkflow && key === 'working' && input.currentStepName) {
    return (
      legacyStepActivityLabels[input.currentStepName] ?? activityLabels[key]
    )
  }
  return activityLabels[key]
}

function resolveBlueprintWorkflowKey(
  input: ImplementationActivityInput,
): RunActivityKey {
  return (
    blueprintWorkflowActivityPriority.find((candidate) =>
      candidate.matches(input),
    )?.key ?? 'working'
  )
}

export function deriveImplementationActivity(
  input: ImplementationActivityInput,
): DerivedImplementationActivity {
  const isBlueprintWorkflow = input.workflowVersion === 3
  const key = isBlueprintWorkflow
    ? resolveBlueprintWorkflowKey(input)
    : legacyKey(input)
  return {
    activity: {
      key,
      label: activityLabel(key, input, isBlueprintWorkflow),
      detail: activityDetail(key, input),
      since: activitySince(key, input),
      resumeAt: activityResumeAt(input),
    },
    availableActions: isBlueprintWorkflow
      ? blueprintWorkflowActions(key, input)
      : [],
  }
}
